import React, { useState, useEffect } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import type {
  UpcomingTraining,
  UpcomingTrainingStatus,
} from "../../types/dashboard.types";
import { dashboardApi } from "../../services/dashboard.api";
import Chip from "@/components/ui/chip";
import { RightChevron } from "@/components/icons";
import styles from "./UpcomingTrainingsTable.module.scss";

const STATUS_CHIP: Record<
  UpcomingTrainingStatus,
  { label: string; type: "success" | "info" | "default" | "warning" }
> = {
  active: { label: "Aktif", type: "success" },
  request_collection: { label: "Talep Toplama", type: "info" },
  closed: { label: i18n.t("upcomingTrainings.statusClosed"), type: "default" },
  approval_pending: { label: "Onay Bekliyor", type: "warning" },
  budget_pending: { label: i18n.t("upcomingTrainings.statusBudgetPending"), type: "warning" },
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export const UpcomingTrainingsTable: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<UpcomingTraining[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dashboardApi
      .getUpcomingTrainings({ page, pageSize })
      .then((res) => {
        setData(res.data);
        setTotal(res.total);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize]);

  const columns: TableColumn<UpcomingTraining>[] = [
    {
      header: t("dashboard.upcomingTrainings.colTrainingName"),
      accessor: (row) => row.trainingName,
      className: styles.colName,
    },
    {
      header: t("dashboard.upcomingTrainings.colFormat"),
      accessor: (row) => row.format,
    },
    {
      header: t("dashboard.upcomingTrainings.colRegistration"),
      accessor: (row) =>
        row.maxRegistrations != null
          ? `${row.registrationCount}/${row.maxRegistrations}`
          : "-",
    },
    {
      header: t("dashboard.upcomingTrainings.colPublicationDate"),
      accessor: (row) => row.publicationDate,
    },
    {
      header: t("dashboard.upcomingTrainings.colStatus"),
      accessor: (row) => {
        const { label, type } = STATUS_CHIP[row.status];
        return (
          <Chip type={type} variant="soft" size="small">
            {label}
          </Chip>
        );
      },
    },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>
          {t("dashboard.upcomingTrainings.title")}
        </h3>
        <button
          type="button"
          className={styles.sectionLink}
          onClick={() => navigate("/dashboard/education-management")}
          aria-label={t("dashboard.upcomingTrainings.viewAll")}
        >
          <RightChevron width={20} height={20} />
        </button>
      </div>
      <Table<UpcomingTraining>
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        totalRecords={total}
        currentPage={page}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        isLoading={loading}
        hasVisibleActions={false}
      />
    </div>
  );
};

export default UpcomingTrainingsTable;
